// src/features/task/form/AttachmentDropzone.tsx
// Files staged before the task exists. Uploaded on submit, then referenced
// from `CreateTask.attachments`.
//
// The drop target is a real <button>: it is in the tab order, Enter and Space
// open the file dialog, and drag-and-drop is the enhancement rather than the
// only way in.

import { useRef, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Icon, FORM_ICONS } from './icons';

export interface PendingFile {
  id: string;
  file: File;
  previewUrl?: string;
}

export interface AttachmentDropzoneProps {
  files: PendingFile[];
  onAdd: (files: FileList | File[]) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentDropzone({ files, onAdd, onRemove, disabled }: AttachmentDropzoneProps) {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  return (
    <div>
      {files.length > 0 && (
        <ul className="mb-3 space-y-2">
          {files.map((item) => (
            <li
              key={item.id}
              className="group flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2"
            >
              {item.previewUrl ? (
                <img src={item.previewUrl} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />
              ) : (
                <Icon d={FORM_ICONS.file} className="h-5 w-5 shrink-0 text-muted-foreground" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-foreground">{item.file.name}</p>
                <p className="text-xs tabular-nums text-muted-foreground">{humanSize(item.file.size)}</p>
              </div>
              <button
                type="button"
                onClick={() => onRemove(item.id)}
                aria-label={t('task.createForm.removeAttachment', { name: item.file.name })}
                className="rounded-sm p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-destructive focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
              >
                <Icon d={FORM_ICONS.x} className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        disabled={disabled}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files.length) onAdd(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={[
          'flex w-full flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-50',
          dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30',
        ].join(' ')}
      >
        <Icon d={FORM_ICONS.upload} className="mb-2 h-6 w-6 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">{t('task.dropFiles')}</span>
      </button>

      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          if (e.target.files?.length) onAdd(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}
